import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Reusable searchable select (combobox) built on shadcn Command + Popover.
 *
 * Props:
 *   items: Array<{
 *     value:    string,            // unique key sent back via onChange
 *     label:    string,            // primary display text (searchable)
 *     sublabel?: string,           // grey secondary text under label (e.g. email, date)
 *     keywords?: string[],         // extra strings to match against when filtering
 *     badge?: { label: string, color: string },  // tiny pill on right of label
 *     group?:   string,            // grouping bucket; items without a group render flat
 *   }>
 *   groupOrder?: string[]          // explicit ordering for groups (others appended after)
 *   value:        string            // currently selected value
 *   onChange:     (value) => void
 *   placeholder?: string            // trigger placeholder when nothing selected
 *   searchPlaceholder?: string      // input placeholder
 *   emptyMessage?: string
 *   className?:   string
 *   disabled?:    boolean
 *   testId?:      string
 */
export function SearchableSelect({
  items,
  groupOrder = [],
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No matches",
  className,
  disabled = false,
  testId,
}) {
  const [open, setOpen] = useState(false);

  const selected = items.find((i) => i.value === value);

  // Group items, preserving order: explicit groupOrder first, then any other groups.
  const groupsMap = new Map();
  for (const item of items) {
    const g = item.group || "";
    if (!groupsMap.has(g)) groupsMap.set(g, []);
    groupsMap.get(g).push(item);
  }
  const orderedGroupKeys = [
    ...groupOrder.filter((g) => groupsMap.has(g)),
    ...[...groupsMap.keys()].filter((g) => !groupOrder.includes(g)),
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", className)}
          disabled={disabled}
          data-testid={testId}
        >
          <span className="truncate text-left">
            {selected ? selected.label : <span className="text-muted-foreground">{placeholder}</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          // include keywords + sublabel in the searchable haystack
          filter={(itemValue, search) => {
            const haystack = (itemValue || "").toLowerCase();
            return haystack.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {orderedGroupKeys.map((groupKey) => {
              const groupItems = groupsMap.get(groupKey) || [];
              if (groupItems.length === 0) return null;
              return (
                <CommandGroup key={groupKey || "_default"} heading={groupKey || undefined}>
                  {groupItems.map((item) => {
                    // Build the searchable haystack: label + sublabel + keywords
                    const searchString = [
                      item.label,
                      item.sublabel || "",
                      ...(item.keywords || []),
                    ].join(" ");
                    return (
                      <CommandItem
                        key={item.value}
                        value={searchString}
                        onSelect={() => {
                          onChange(item.value);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 shrink-0",
                            value === item.value ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="flex items-center gap-2 truncate">
                            <span className="truncate">{item.label}</span>
                            {item.badge && (
                              <span
                                className={cn(
                                  "text-[10px] px-1.5 py-0.5 rounded font-mono shrink-0",
                                  item.badge.color
                                )}
                              >
                                {item.badge.label}
                              </span>
                            )}
                          </span>
                          {item.sublabel && (
                            <span className="text-xs text-muted-foreground truncate">
                              {item.sublabel}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
